import { useState } from "react";
import { styles } from "../styles";
import { createJob } from "../models/models";
import { jobTypeOptions } from "../utils/constants";
import { hasCapability } from "../utils/capabilities";
import BackButton from "../components/BackButton";
import EquipmentMultiSelect from "../components/EquipmentMultiSelect";

// "open" is the umbrella for any non-closed job. The data model still
// stores distinct "unassigned" and "assigned" stages on each job (handy
// for filtering elsewhere and for the request-acceptance flow), but on
// the dashboard the two collapse into a single "In Queue" tile since
// assigning a tech and starting a job are the same workflow from the
// dispatcher's view. "in_queue" is a VIRTUAL UI-only status — never
// written to a job doc — that the tile filter expands into either
// underlying status.
const STATUS_ORDER = [
  "requested",
  "in_queue",
  "in_progress",
  "closed",
  "canceled",
];
// Labels cover the tile statuses AND the underlying data-model statuses
// (unassigned / assigned) so the edit dropdown — which writes the
// underlying value to job.status — can still pick a human label off this
// map. STATUS_ORDER only drives the dashboard tile render order.
const STATUS_LABEL = {
  requested: "Requested",
  in_queue: "In Queue",
  unassigned: "Unassigned",
  assigned: "Assigned",
  in_progress: "In Progress",
  closed: "Completed",
  canceled: "Canceled",
};
// Maps a tile status (incl. the virtual "in_queue") to the underlying
// data-model status(es) that count toward its bucket.
const STATUS_MATCHES = {
  in_queue: ["unassigned", "assigned"],
};
// Statuses the edit dropdown can WRITE to job.status. "in_queue" is
// excluded — it's a UI bucket, not a valid stored value. "requested"
// stays out too: requests come in via the cross-org flow and aren't
// hand-set from the edit form.
const EDIT_STATUS_OPTIONS = [
  "unassigned",
  "assigned",
  "in_progress",
  "closed",
  "canceled",
];

export default function JobsPage({
  customers,
  jobsRaw,
  actions,
  role,
  capabilities,
  user,
  setSelectedJob,
  onBack,
}) {
  const canCreateJobs = hasCapability(role, capabilities, "createJobs");
  const canEditJobs = hasCapability(role, capabilities, "editJobs");
  const canDeleteJobs = hasCapability(role, capabilities, "deleteJobs");

  const [search, setSearch] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [jobType, setJobType] = useState("");
  // Optional override of the auto-assigned YYYY#### job number. If left
  // blank, the addJob action falls back to the counter transaction. Use
  // this when the boss / customer requires a specific number (their PO
  // reference, owner's internal job code, etc.). Don't pick something
  // that looks like the auto format YYYY####.
  const [customJobNumber, setCustomJobNumber] = useState("");
  // For externally-quoted jobs (quote done outside the app) — locks the
  // customer-facing bill price exactly the way approveQuoteAndCreateJob
  // does. Only collected when jobType === "Quoted".
  const [quotedAmount, setQuotedAmount] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [locationId, setLocationId] = useState("");
  const [equipmentIds, setEquipmentIds] = useState([]);
  const [updateUponDiagnostic, setUpdateUponDiagnostic] = useState(false);

  const [editingJobId, setEditingJobId] = useState(null);
  const [editTitle, setEditTitle] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editJobType, setEditJobType] = useState("");
  const [editStatus, setEditStatus] = useState("");

  // Only one status section open at a time. "In Progress" opens by
  // default. Clicking another section opens it (closes the previous);
  // clicking the open one closes it (no section open).
  const [openSection, setOpenSection] = useState("in_progress");
  const toggleSection = (s) =>
    setOpenSection((current) => (current === s ? null : s));

  const startEditJob = (job) => {
    setEditingJobId(job.id);
    setEditTitle(job.title || "");
    setEditDescription(job.description || "");
    setEditJobType(job.jobType || "");
    setEditStatus(job.status || "unassigned");
  };

  const saveEditJob = async () => {
    if (!editTitle || !editingJobId) return;
    const patch = {
      title: editTitle,
      description: editDescription,
      jobType: editJobType,
      status: editStatus,
      updatedAt: new Date().toISOString(),
    };
    if (editStatus === "closed") {
      patch.closedAt = new Date().toISOString();
      patch.closedBy = user?.email || "unknown";
    }
    await actions.updateJob(editingJobId, patch);
    setEditingJobId(null);
  };

  const handleCancelJob = async (job) => {
    const confirmed = window.confirm(
      `Cancel job "${job.title}"?\n\nIt moves to the Canceled section — NOT deleted. You can reopen it later by editing its status.`
    );
    if (!confirmed) return;
    await actions.updateJob(job.id, {
      status: "canceled",
      updatedAt: new Date().toISOString(),
    });
  };

  const handleAcceptRequest = async (job) => {
    await actions.acceptRequestedJob(job.id);
  };

  const handleDeclineRequest = async (job) => {
    const confirmed = window.confirm(
      `Decline this service request from ${job.requestedByName || "the facility"}?\n\nIt moves to Canceled (kept as a record), not deleted.`
    );
    if (!confirmed) return;
    await actions.updateJob(job.id, {
      status: "canceled",
      updatedAt: new Date().toISOString(),
    });
  };

  const handleDeleteJob = async (job) => {
    const confirmed = window.confirm(
      `Permanently DELETE job "${job.title}"?\n\nThis removes it entirely and cannot be undone. To keep a record, use Cancel Job instead.`
    );
    if (!confirmed) return;
    await actions.deleteJob(job.id);
  };

  const selectedCustomer = customers.find((c) => c.id === customerId);
  const locationsForCustomer = selectedCustomer?.locations || [];
  const selectedLocation = locationsForCustomer.find((l) => l.id === locationId);
  const equipmentForLocation = selectedLocation?.equipment || [];

  const resetForm = () => {
    setTitle("");
    setDescription("");
    setJobType("");
    setQuotedAmount("");
    setCustomJobNumber("");
    setCustomerId("");
    setLocationId("");
    setEquipmentIds([]);
    setUpdateUponDiagnostic(false);
  };

  const toggleEquipment = (eqId) => {
    setEquipmentIds((prev) =>
      prev.includes(eqId) ? prev.filter((id) => id !== eqId) : [...prev, eqId]
    );
  };

  const handleCreate = async () => {
    if (!title || !customerId || !locationId) return;
    const isQuoted = jobType === "Quoted";
    const quotedNum = parseFloat(quotedAmount);
    if (isQuoted && (!Number.isFinite(quotedNum) || quotedNum <= 0)) {
      alert("Enter the quoted price (greater than 0) for this job.");
      return;
    }
    const baseJob = createJob(title);
    const job = {
      ...baseJob,
      // If the form has a non-blank custom job number, override the auto
      // value baseJob initialized; the addJob action treats this as
      // "use verbatim, skip the counter."
      ...(customJobNumber.trim() && { jobNumber: customJobNumber.trim() }),
      description,
      jobType,
      customerId,
      locationId,
      equipmentIds,
      updateUponDiagnostic,
      createdBy: user?.email || "unknown",
      // External-quote path: lock the customer price on the bill the same
      // way approveQuoteAndCreateJob does. Bill lines stay empty so the
      // P&L variance reflects only actuals from tickets/POs.
      ...(isQuoted && {
        costing: {
          ...(baseJob.costing || {}),
          labor: [],
          materials: [],
          expenses: [],
          fuel: [],
          taxes: [],
          billDescription: description || "",
          priceMode: "quoted",
          quotedAmount: quotedNum,
        },
      }),
    };
    await actions.addJob(job);
    resetForm();
    setShowCreate(false);
  };

  const customerName = (id) =>
    customers.find((c) => c.id === id)?.name || "—";
  const locationName = (cId, lId) => {
    const c = customers.find((x) => x.id === cId);
    return c?.locations.find((l) => l.id === lId)?.name || "—";
  };

  // Build the searchable text for a job: its own fields plus the names of
  // the customer/location and the type/sub-type/model/serial/part numbers
  // of every piece of equipment attached to it.
  const matchesSearch = (job) => {
    const term = search.trim().toLowerCase();
    if (!term) return true;
    const cust = customers.find((c) => c.id === job.customerId);
    const loc = cust?.locations.find((l) => l.id === job.locationId);
    const equipment = (loc?.equipment || []).filter((eq) =>
      (job.equipmentIds || []).includes(eq.id)
    );
    const haystack = [
      job.title,
      job.description,
      job.jobType,
      cust?.name,
      loc?.name,
      ...equipment.flatMap((eq) => [
        eq.type,
        eq.subType,
        eq.manufacturer,
        eq.modelNumber,
        eq.serialNumber,
        eq.partsList,
      ]),
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    return haystack.includes(term);
  };

  // Closed jobs with a finalized bill move off the board into the
  // archive (a separate page will land post-demo). Until then they're
  // just hidden from the JobsPage so the Completed section reflects
  // only what still needs a final bill.
  const jobsByStatus = (status) => {
    // Virtual "in_queue" tile matches multiple underlying statuses;
    // every other tile matches one. Default missing job.status to
    // "unassigned" so legacy data still bucketed correctly.
    const matches = STATUS_MATCHES[status] || [status];
    return [...jobsRaw]
      .filter((j) => matches.includes(j.status || "unassigned"))
      .filter((j) => !j.billFinalized)
      .filter(matchesSearch)
      .sort(
        (a, b) =>
          new Date(b.createdAt || 0) - new Date(a.createdAt || 0)
      );
  };

  return (
    <>
      <BackButton onClick={onBack} />

      <h2>Jobs</h2>

      <input
        style={styles.input}
        placeholder="Search jobs by name, keyword, customer, location, or part number"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />

      {canCreateJobs && (
        <>
          <button
            style={styles.button}
            onClick={() => setShowCreate(!showCreate)}
          >
            {showCreate ? "Hide New Job" : "New Job"}
          </button>

          {showCreate && (
            <div style={styles.card}>
              {/* Optional custom job number. Blank → auto-assign YYYY####
                  via the addJob counter transaction. Filled → used verbatim
                  (e.g. "ACME-PO-4521" or your boss's internal code). */}
              <input
                style={styles.input}
                placeholder="Custom job number (optional — leave blank for auto)"
                value={customJobNumber}
                onChange={(e) => setCustomJobNumber(e.target.value)}
              />
              <input
                style={styles.input}
                placeholder="Job title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
              <textarea
                style={styles.textarea}
                placeholder="Description (what needs doing?)"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
              <br />
              <label>Job Type: </label>
              <select
                value={jobType}
                onChange={(e) => setJobType(e.target.value)}
              >
                <option value="">Select type</option>
                {jobTypeOptions.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
              <br />
              {jobType === "Quoted" && (
                <div
                  style={{
                    marginTop: "6px",
                    padding: "8px 10px",
                    background: "#fff7e6",
                    border: "1px solid #f0c674",
                    borderRadius: "4px",
                    fontSize: "13px",
                  }}
                >
                  <label>
                    Quoted price (customer-locked): $
                    <input
                      style={{ ...styles.input, width: "120px" }}
                      type="number"
                      step="0.01"
                      placeholder="0.00"
                      value={quotedAmount}
                      onChange={(e) => setQuotedAmount(e.target.value)}
                    />
                  </label>
                  <div style={{ fontSize: "12px", color: "#5a4000", marginTop: "4px" }}>
                    Use this for jobs quoted outside the app. The bill stays
                    locked at this price; tickets and POs still feed labor /
                    material costs for internal P&amp;L.
                  </div>
                </div>
              )}
              <label>Customer: </label>
              <select
                value={customerId}
                onChange={(e) => {
                  setCustomerId(e.target.value);
                  setLocationId("");
                  setEquipmentIds([]);
                }}
              >
                <option value="">Select customer</option>
                {[...customers]
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
              </select>
              <br />

              {customerId && (
                <>
                  <label>Location: </label>
                  <select
                    value={locationId}
                    onChange={(e) => {
                      setLocationId(e.target.value);
                      setEquipmentIds([]);
                    }}
                  >
                    <option value="">Select location</option>
                    {locationsForCustomer.map((l) => (
                      <option key={l.id} value={l.id}>
                        {l.name}
                      </option>
                    ))}
                  </select>
                  <br />
                </>
              )}

              {locationId && (
                <div style={{ marginTop: "8px" }}>
                  <strong style={{ fontSize: "12px", display: "block", marginBottom: "4px" }}>
                    Equipment (select one or more):
                  </strong>
                  <EquipmentMultiSelect
                    equipment={equipmentForLocation}
                    selectedIds={equipmentIds}
                    onChange={setEquipmentIds}
                  />
                </div>
              )}

              {locationId && (
                <label
                  style={{
                    display: "block",
                    fontSize: "12px",
                    marginTop: "8px",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={updateUponDiagnostic}
                    onChange={(e) => setUpdateUponDiagnostic(e.target.checked)}
                  />{" "}
                  Update upon Diagnostic (equipment/scope confirmed after the
                  on-site visit)
                </label>
              )}

              <br />
              <button
                style={styles.button}
                onClick={handleCreate}
                disabled={!title || !customerId || !locationId}
              >
                Create Job
              </button>
            </div>
          )}
        </>
      )}

      {/* Status tiles — click one to toggle its jobs below */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
          gap: "10px",
          margin: "12px 0",
        }}
      >
        {STATUS_ORDER.map((status) => {
          const count = jobsByStatus(status).length;
          const active = openSection === status;
          return (
            <div
              key={status}
              onClick={() => toggleSection(status)}
              style={{
                ...styles.card,
                cursor: "pointer",
                textAlign: "center",
                border: active ? "2px solid #1a5fb4" : styles.card.border,
                background: active ? "#eef4fc" : undefined,
              }}
            >
              <strong style={{ fontSize: "13px" }}>
                {STATUS_LABEL[status]}
              </strong>
              <div style={{ fontSize: "26px" }}>{count}</div>
            </div>
          );
        })}
      </div>

      {/* Job lists for the open status tiles */}
      {STATUS_ORDER.filter((status) => openSection === status).map(
        (status) => {
          const jobs = jobsByStatus(status);
          return (
            <div key={status}>
              <h3>
                {STATUS_LABEL[status]} ({jobs.length})
              </h3>
              {jobs.length === 0 ? (
                <p style={{ fontSize: "12px", color: "#666" }}>None.</p>
              ) : (
                jobs.map((job) => (
                  <div key={job.id} style={styles.card}>
                    {editingJobId === job.id ? (
                    <>
                      <input
                        style={styles.input}
                        placeholder="Job title"
                        value={editTitle}
                        onChange={(e) => setEditTitle(e.target.value)}
                      />
                      <textarea
                        style={styles.textarea}
                        placeholder="Description"
                        value={editDescription}
                        onChange={(e) => setEditDescription(e.target.value)}
                      />
                      <br />
                      <label>Job Type: </label>
                      <select
                        value={editJobType}
                        onChange={(e) => setEditJobType(e.target.value)}
                      >
                        <option value="">Select type</option>
                        {jobTypeOptions.map((t) => (
                          <option key={t} value={t}>
                            {t}
                          </option>
                        ))}
                      </select>
                      <br />
                      <label>Status: </label>
                      <select
                        value={editStatus}
                        onChange={(e) => setEditStatus(e.target.value)}
                      >
                        {EDIT_STATUS_OPTIONS.map((s) => (
                          <option key={s} value={s}>
                            {STATUS_LABEL[s]}
                          </option>
                        ))}
                      </select>
                      <br />
                      <button style={styles.button} onClick={saveEditJob}>
                        Save
                      </button>
                      <button
                        style={styles.button}
                        onClick={() => setEditingJobId(null)}
                      >
                        Cancel Edit
                      </button>
                    </>
                  ) : (
                    <>
                      <strong
                        style={{ cursor: "pointer", color: "#1a5fb4" }}
                        onClick={() => setSelectedJob(job)}
                      >
                        {job.jobNumber ? `${job.jobNumber} — ` : ""}
                        {job.title}
                      </strong>
                      {job.jobType && (
                        <span
                          style={{
                            fontSize: "11px",
                            marginLeft: "8px",
                            padding: "1px 6px",
                            background: "#e0e0e0",
                            borderRadius: "4px",
                          }}
                        >
                          {job.jobType}
                        </span>
                      )}
                      {job.isMaintenanceVisit && job.scheduledDate && (
                        <span
                          style={{
                            fontSize: "11px",
                            marginLeft: "6px",
                            padding: "1px 6px",
                            background: "#e0eaff",
                            color: "#1a5fb4",
                            border: "1px solid #bcd0ee",
                            borderRadius: "4px",
                            fontWeight: 600,
                          }}
                          title="Scheduled visit date under maintenance contract"
                        >
                          Scheduled: {job.scheduledDate}
                        </span>
                      )}
                      <br />
                      <span style={{ fontSize: "12px", color: "#666" }}>
                        {customerName(job.customerId)} /{" "}
                        {locationName(job.customerId, job.locationId)}
                        {" • "}
                        {(job.equipmentIds || []).length} equipment
                        {" • "}
                        {(job.assignedTechs || []).length === 0
                          ? "Unassigned"
                          : `${job.assignedTechs.length} tech(s)`}
                      </span>
                      {job.description && (
                        <>
                          <br />
                          <span style={{ fontSize: "12px" }}>
                            {job.description}
                          </span>
                        </>
                      )}
                      {job.status === "requested" && (
                        <>
                          <br />
                          <span style={{ fontSize: "12px", color: "#1a5fb4" }}>
                            Requested by {job.requestedByName || "facility"}
                          </span>
                          {canEditJobs && (
                            <div style={{ marginTop: "8px" }}>
                              <button
                                style={styles.button}
                                onClick={() => handleAcceptRequest(job)}
                              >
                                Accept
                              </button>
                              <button
                                style={styles.button}
                                onClick={() => handleDeclineRequest(job)}
                              >
                                Decline
                              </button>
                            </div>
                          )}
                        </>
                      )}
                      {(canEditJobs || canDeleteJobs) && (
                        <div style={{ marginTop: "8px" }}>
                          {canEditJobs && (
                            <button
                              style={styles.button}
                              onClick={() => startEditJob(job)}
                            >
                              Edit
                            </button>
                          )}
                          {canEditJobs &&
                            job.status !== "canceled" &&
                            job.status !== "closed" && (
                              <button
                                style={styles.button}
                                onClick={() => handleCancelJob(job)}
                              >
                                Cancel Job
                              </button>
                            )}
                          {canDeleteJobs && (
                            <button
                              style={{ ...styles.button, color: "red" }}
                              onClick={() => handleDeleteJob(job)}
                            >
                              Delete
                            </button>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>
              ))
            )}
          </div>
        );
      })}
    </>
  );
}
